import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HelpCircle, BookOpen, MessageCircle, X, Map, Brain, Plug,
  Zap, BarChart3, Users, ChevronRight, Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { tutorials, getTutorialsForRoute } from '@/lib/tutorial-data';
import { TutorialOverlay } from '@/components/TutorialOverlay';
import { AICoachPanel } from '@/components/AICoachPanel';
import type { Tutorial } from '@/lib/tutorial-data';

const ICON_MAP: Record<string, any> = {
  Map, Brain, Plug, Zap, BarChart3, Users,
};

const CATEGORY_LABELS = {
  platform: 'Platform',
  intelligence: 'Intelligence',
  analytics: 'Analytics',
  settings: 'Settings',
};

export function HelpLauncher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'menu' | 'tutorials' | 'coach'>('menu');
  const [activeTutorial, setActiveTutorial] = useState<Tutorial | null>(null);
  const [completedTutorials, setCompletedTutorials] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('bw_completed_tutorials');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  const markComplete = (id: string) => {
    setCompletedTutorials(prev => {
      const next = new Set(prev).add(id);
      localStorage.setItem('bw_completed_tutorials', JSON.stringify([...next]));
      return next;
    });
  };

  const startTutorial = (t: Tutorial) => {
    setActiveTutorial(t);
    setOpen(false);
    setMode('menu');
  };

  const openCoach = () => {
    setMode('coach');
    setOpen(false);
  };

  // Group tutorials by category
  const grouped = tutorials.reduce((acc, t) => {
    (acc[t.category] ??= []).push(t);
    return acc;
  }, {} as Record<string, Tutorial[]>);

  return (
    <>
      {/* Floating help button */}
      {!activeTutorial && mode !== 'coach' && (
        <button
          onClick={() => { setOpen(!open); setMode(open ? 'menu' : 'menu'); }}
          className={cn(
            'fixed bottom-[124px] right-[24px] z-[9990] h-9 w-9 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ring-2 ring-background',
            open
              ? 'bg-muted text-foreground rotate-90'
              : 'bg-primary text-primary-foreground hover:scale-105'
          )}
          aria-label="Help & Tutorials"
        >
          {open ? <X className="h-5 w-5" /> : <HelpCircle className="h-5 w-5" />}
        </button>
      )}

      {/* Menu popup */}
      {open && mode === 'menu' && (
        <div className="fixed bottom-20 right-5 z-[9991] w-72 bg-card border border-border rounded-2xl shadow-2xl animate-in slide-in-from-bottom-3 fade-in-0 duration-200 overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground text-sm">How can we help?</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Tutorials & AI-assisted guidance</p>
          </div>
          <div className="p-2 space-y-1">
            <button
              onClick={() => setMode('tutorials')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
            >
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Step-by-Step Tutorials</p>
                <p className="text-xs text-muted-foreground">Guided walkthroughs of every feature</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
            <button
              onClick={openCoach}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
            >
              <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center">
                <MessageCircle className="h-4 w-4 text-success" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">AI Coach</p>
                <p className="text-xs text-muted-foreground">Ask anything, get instant answers</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Tutorial list */}
      {open && mode === 'tutorials' && (
        <div className="fixed bottom-20 right-5 z-[9991] w-80 bg-card border border-border rounded-2xl shadow-2xl animate-in slide-in-from-bottom-3 fade-in-0 duration-200 overflow-hidden max-h-[70vh] flex flex-col">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <button onClick={() => setMode('menu')} className="text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-4 w-4 rotate-180" />
            </button>
            <h3 className="font-semibold text-foreground text-sm">Tutorials</h3>
            <span className="ml-auto text-xs text-muted-foreground">
              {completedTutorials.size}/{tutorials.length} done
            </span>
          </div>
          <div className="overflow-y-auto p-2 space-y-3">
            {/* Page-relevant tutorials shown first */}
            {(() => {
              const pageRelevant = getTutorialsForRoute(location.pathname).filter(t => !completedTutorials.has(t.id));
              if (pageRelevant.length === 0) return null;
              return (
                <div>
                  <p className="px-3 py-1 text-[10px] font-semibold text-primary uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> For this page
                  </p>
                  {pageRelevant.map(t => {
                    const Icon = ICON_MAP[t.icon] || BookOpen;
                    return (
                      <button
                        key={t.id}
                        onClick={() => startTutorial(t)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors text-left border border-primary/10"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{t.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{t.description}</p>
                        </div>
                        <span className="text-[10px] text-primary/70 shrink-0">{t.steps.length} steps</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {Object.entries(grouped).map(([cat, items]) => {
              const sorted = [...items].sort((a, b) => {
                const aDone = completedTutorials.has(a.id) ? 1 : 0;
                const bDone = completedTutorials.has(b.id) ? 1 : 0;
                return aDone - bDone;
              });
              return (
                <div key={cat}>
                  <p className="px-3 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                    {CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS]}
                  </p>
                  {sorted.map(t => {
                    const Icon = ICON_MAP[t.icon] || BookOpen;
                    const done = completedTutorials.has(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => startTutorial(t)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                      >
                        <Icon className={cn('h-4 w-4 shrink-0', done ? 'text-success' : 'text-muted-foreground')} />
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium', done ? 'text-muted-foreground line-through' : 'text-foreground')}>
                            {t.title}
                            {done && <span className="ml-1.5 text-[10px] text-success no-underline">✓</span>}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{t.description}</p>
                        </div>
                        <span className="text-[10px] text-muted-foreground/50 shrink-0">{t.steps.length} steps</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Coach panel */}
      {mode === 'coach' && (
        <>
          <AICoachPanel onClose={() => setMode('menu')} />
          <button
            onClick={() => setMode('menu')}
            className="fixed bottom-5 right-5 z-[9999] h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform ring-2 ring-background"
            aria-label="Close AI Coach"
          >
            <X className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Active tutorial overlay */}
      {activeTutorial && (
        <TutorialOverlay
          tutorial={activeTutorial}
          onClose={() => setActiveTutorial(null)}
          onComplete={markComplete}
        />
      )}
    </>
  );
}
